import {
  Layers, TrendingUp,
  Users, BookOpen, ScrollText, Mail, Info,
  Sparkles, Calculator, Award, FileCheck, ShieldCheck,
  LineChart, BarChart3, CandlestickChart, Banknote, Wallet,
  Smartphone, Monitor, Coins, Bitcoin, Fuel, Activity, Crown, Star,
  Headphones, Newspaper, Globe, Cpu, Zap, Clock, Compass, Building2,
  Gauge, Apple, Lock, Cookie, BadgeDollarSign, Scale, Moon,
  Handshake, Megaphone, BadgeCheck, UserPlus,
} from "lucide-react";
import OFinIcon from "@/components/icons/OFinIcon";

/* ===== OFinancial Markets · neat & organised mega menu =====
   4 top items · Trading · Accounts · Platforms · Company.
   3 columns per section (consistent grid density, no wide empty
   stretches), short taglines, small gaps. Spotlight CTA cards on the
   right reinforce the highest-intent destination per section. */
export const MEGA_MENU = [
  /* ============== TRADING ============== */
  {
    key: "trading",
    label: "Trading",
    columns: [
      {
        heading: "FX & Commodities",
        items: [
          { to: "/markets/forex",    Icon: LineChart,        label: "Forex",    tagline: "78+ pairs · 0.0 pips" },
          { to: "/markets/metals",   Icon: Coins,            label: "Metals",   tagline: "Gold · silver · platinum" },
          { to: "/markets/energies", Icon: Fuel,             label: "Energies", tagline: "Brent · WTI · gas" },
        ],
      },
      {
        heading: "Equities & Crypto",
        items: [
          { to: "/markets/indices",  Icon: BarChart3,        label: "Indices",  tagline: "Global cash & futures" },
          { to: "/markets/shares",   Icon: CandlestickChart, label: "Shares",   tagline: "US · EU · UK · Asia" },
          { to: "/markets/crypto",   Icon: Bitcoin,          label: "Crypto",   tagline: "BTC · ETH · 24/7" },
        ],
      },
      {
        heading: "Tools & Insights",
        items: [
          { to: "/live-spreads",      Icon: Activity,   label: "Live Spreads",     tagline: "Real-time pricing", highlight: true },
          { to: "/calculator",        Icon: Calculator, label: "Trading Calculator", tagline: "Pip · margin · swap" },
          { to: "/symbols",           Icon: Layers,     label: "Symbol Details",    tagline: "Pip values · lot sizes" },
          { to: "/economic-calendar", Icon: Newspaper,  label: "Economic Calendar", tagline: "High-impact news" },
          { to: "/sentiment",         Icon: Compass,    label: "Market Sentiment",  tagline: "Crowd positioning" },
          { to: "/market-hours",      Icon: Globe,      label: "Market Hours",      tagline: "Sessions worldwide" },
        ],
      },
      {
        heading: "Resources",
        items: [
          { to: "/glossary",      Icon: ScrollText,label: "Trading Glossary", tagline: "Terms explained" },
          { to: "/leverage",      Icon: Gauge,     label: "Leverage",      tagline: "Up to 1:1000 dynamic" },
          { to: "/swap-rates",    Icon: Clock,     label: "Swap Rates",    tagline: "Transparent overnight" },
          { to: "/faq",           Icon: FileCheck, label: "FAQ",           tagline: "Common questions" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "Live data",
      title: "Live spreads on every asset",
      body: "Direct MT5 bridge · sub-millisecond pricing across FX, metals, indices, energies and shares.",
      cta: { label: "See live spreads", to: "/live-spreads" },
      icon: TrendingUp,
    },
  },

  /* ============== ACCOUNTS ============== */
  {
    key: "accounts",
    label: "Accounts",
    columns: [
      {
        heading: "Standard Tiers",
        items: [
          { to: "/account-types/classic", Icon: Star,        label: "Classic",  tagline: "$50 · 1.0 pip · $0 fee" },
          { to: "/account-types/premier", Icon: Crown,       label: "Premier",  tagline: "$1K · 0.0 pip · $3.00", highlight: true },
          { to: "/account-types/elite",   Icon: Sparkles,    label: "Elite",    tagline: "$5K · 0.0 pip · $2.5", premium: true },
          { to: "/account-types/cent",    Icon: Coins,       label: "Cent",     tagline: "$50 · USC · micro-lot" },
        ],
      },
      {
        heading: "Specialised",
        items: [
          { to: "/account-types/social",  Icon: OFinIcon, label: "Social Trading", tagline: "Copy top traders" },
          { to: "/account-types/islamic", Icon: Moon,     label: "Islamic",           tagline: "Swap-free accounts" },
          { to: "/account-comparison",    Icon: Layers,   label: "Compare All",       tagline: "Side-by-side comparison" },
        ],
      },
      {
        heading: "Funding",
        items: [
          { to: "/first-deposit-bonus", Icon: Sparkles, label: "50% Bonus",       tagline: "First deposit boost", promo: true },
          { to: "/funding",             Icon: Banknote, label: "Funding Methods", tagline: "Card · wire · crypto" },
        ],
      },
      {
        heading: "Withdrawals & Safety",
        items: [
          { to: "/withdrawals",     Icon: Wallet,      label: "Withdrawals",     tagline: "Same-day local channels" },
          { to: "/safety-of-funds", Icon: ShieldCheck, label: "Safety of Funds", tagline: "Segregated accounts" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "Limited offer",
      title: "50% bonus on first deposit",
      body: "Boost your trading capital by up to USD 5,000 · credited within 24 working hours after deposit.",
      cta: { label: "Claim bonus", to: "/first-deposit-bonus" },
      icon: Sparkles,
    },
  },

  /* ============== PLATFORMS ============== */
  {
    key: "platforms",
    label: "Platforms",
    columns: [
      {
        heading: "MetaTrader 5",
        items: [
          { to: "/platforms/mt5",         IcomoonName: "mt5", Icon: Layers, label: "MT5 Overview", tagline: "1200+ instruments" },
          { to: "/platforms/mt5/web",     Icon: Monitor,    label: "Web Terminal", tagline: "Trade in browser" },
          { to: "/platforms/mt5/desktop", Icon: Monitor,    label: "Desktop",      tagline: "Windows & macOS" },
          { to: "/platforms/mt5/mobile",  Icon: Smartphone, label: "Mobile",       tagline: "iOS & Android" },
        ],
      },
      {
        heading: "Mobile Apps",
        items: [
          { to: "/platforms/mt5/mobile",  Icon: Smartphone, label: "iOS App",     tagline: "Trade on iPhone" },
          { to: "/platforms/mt5/mobile",  Icon: Apple,      label: "Android App", tagline: "Trade on Android" },
        ],
      },
      {
        heading: "Automation",
        items: [
          { to: "/platforms/algo",         Icon: Cpu, label: "Algo Trading",  tagline: "MQL5 expert advisors" },
          { to: "/platforms/vps",          Icon: Zap, label: "Free VPS",      tagline: "24/7 EA uptime" },
        ],
      },
      {
        heading: "Community",
        items: [
          { to: "/platforms/copy-trading", Icon: Users, label: "Copy Trading",  tagline: "Mirror top traders" },
          { to: "/account-types/social",   Icon: OFinIcon, label: "Social Trading", tagline: "In your Trader's Room" },
        ],
      },
    ],
    spotlight: {
      kind: "platforms",
      eyebrow: "One platform · all markets",
      title: "MT5 · institutional execution",
      body: "Desktop, web, mobile. Cloud-saved layouts. MQL5 EAs supported. Identical execution across devices.",
      cta: { label: "Open MT5 Web", to: "/platforms/mt5/web" },
      icon: ShieldCheck,
    },
  },

  /* ============== PARTNERS ============== */
  {
    key: "partners",
    label: "Partners",
    columns: [
      {
        heading: "Programmes",
        items: [
          { to: "/partner#introducer", Icon: Handshake,  label: "Introducer (IB)", tagline: "Up to 50% rev-share", highlight: true },
          { to: "/partner#ambassador", Icon: BadgeCheck, label: "Ambassador",      tagline: "Fixed fee + spread" },
        ],
      },
      {
        heading: "Tools",
        items: [
          { to: "/partner#apply", Icon: Calculator, label: "Earnings Calculator", tagline: "Project monthly income" },
          { to: "/contact",       Icon: Megaphone,  label: "Custom Schemes",      tagline: "Bespoke deals" },
        ],
      },
      {
        heading: "Get Started",
        items: [
          { to: "/partner", Icon: UserPlus, label: "Become a Partner", tagline: "Apply in minutes", highlight: true },
        ],
      },
      {
        heading: "Talk to Us",
        items: [
          { to: "/contact", Icon: Mail, label: "Partnership Team", tagline: "Speak to a manager" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "Refer & Earn",
      title: "Up to 50% spread revenue share",
      body: "Daily payouts on the Introducer programme. Fixed monthly fee + spread share on Ambassador. Bespoke deals for larger networks. Transparent and straightforward.",
      cta: { label: "Become a partner", to: "/partner" },
      icon: Handshake,
    },
  },

  /* ============== COMPANY ============== */
  {
    key: "company",
    label: "Company",
    columns: [
      {
        heading: "About",
        items: [
          { to: "/about",          Icon: Info,      label: "About OFinancial", tagline: "Global broker" },
          { to: "/why-ofinancial", Icon: Award,     label: "Why OFinancial",   tagline: "DMA · STP execution" },
          { to: "/company-news",   Icon: Newspaper, label: "Company News",     tagline: "Press & announcements" },
        ],
      },
      {
        heading: "Trust",
        items: [
          { to: "/regulation",      Icon: ShieldCheck, label: "Regulation",      tagline: "Global coverage" },
          { to: "/safety-of-funds", Icon: Building2,   label: "Safety of Funds", tagline: "Tier-1 segregated" },
        ],
      },
      {
        heading: "Support",
        items: [
          { to: "/contact", Icon: Mail,       label: "Contact",     tagline: "Reach our team" },
          { to: "/faq",     Icon: FileCheck,  label: "FAQ",         tagline: "Common questions" },
          { to: "/support", Icon: Headphones, label: "Help Centre", tagline: "24/5 client support" },
        ],
      },
      {
        heading: "Legal",
        items: [
          { to: "/legal",                  Icon: ScrollText,     label: "Legal Centre", tagline: "All policies", highlight: true },
          { to: "/legal/terms",            Icon: Scale,          label: "Terms",        tagline: "Master agreement" },
          { to: "/legal/privacy",          Icon: Lock,           label: "Privacy",      tagline: "Data protection" },
          { to: "/legal/bonus-terms",      Icon: BadgeDollarSign,label: "Bonus Terms",  tagline: "Promo conditions" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "Regulated broker",
      title: "Global coverage · Licensed",
      body: "Our financial group is authorized and licensed in Antigua and Mauritius. Client funds segregated in tier-1 banks.",
      cta: { label: "Learn more", to: "/regulation" },
      icon: ShieldCheck,
    },
  },
];
